import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link } from 'react-router';
import { Building2, ChevronLeft, MapPin } from 'lucide-react';
import type { OwnerRegistration, Tenant, TenantOwner, VenueProfile } from '@kratispot/shared';
import {
  ApiError,
  createVenue,
  removeVenueLogo,
  fetchTenants,
  registerOwner,
  renameOwner,
  removeOwner,
  updateVenue,
  uploadVenueLogo,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Toasts, useToasts } from '@/components/toasts';
import { OwnerDialog } from '@/components/owner-dialog';
import { VenueCard, typeLabel } from '@/components/venue-card';
import { VenueDialog, type LogoChange } from '@/components/venue-dialog';
import { useTranslation, type CopyKey } from '@/lib/i18n';
import { keptNameNote } from '@/lib/kept-name';
import { cn } from '@/lib/utils';

// A refused save says which of its two steps refused it, and why. §3
export function refusalKey(caught: unknown, stage: 'profile' | 'logo'): CopyKey {
  if (stage === 'logo') return 'logoFailed';
  return caught instanceof ApiError && caught.status === 409 ? 'slugTaken' : 'venueSaveFailed';
}

// Editing means an existing tenant; creating is the same form with none. See §3.
type Editing = { tenant: Tenant | null } | null;

// Types are venue data, so the chips are whatever the platform actually holds.
export function categoriesOf(tenants: readonly Tenant[]): string[] {
  return [...new Set(tenants.map((tenant) => tenant.venueType))].sort();
}

export function PlatformPage({
  onOpenVenue,
  onVenuesChanged,
}: {
  // The slug, not the id: it is what the address carries. §8
  onOpenVenue: (slug: string) => void;
  onVenuesChanged: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [phase, setPhase] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [category, setCategory] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing>(null);
  const [owning, setOwning] = useState<Tenant | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toasts, push } = useToasts();

  const load = useCallback(
    (signal?: AbortSignal): Promise<Tenant[]> =>
      fetchTenants(signal)
        .then((response) => {
          setTenants(response.tenants);
          setPhase('ready');
          return response.tenants;
        })
        .catch(() => {
          if (!signal?.aborted) setPhase('failed');
          return [];
        }),
    [],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const categories = useMemo(() => categoriesOf(tenants), [tenants]);
  const shown = tenants.filter((tenant) => category === null || tenant.venueType === category);

  // §3: the logo follows the profile, because a new venue has no id before it.
  async function save(profile: VenueProfile, logo: LogoChange): Promise<void> {
    const tenant = editing?.tenant ?? null;
    let stage: 'profile' | 'logo' = 'profile';
    setBusy(true);
    setError(null);
    try {
      const saved = await (tenant === null
        ? createVenue(profile)
        : updateVenue(tenant.venueId, profile));
      stage = 'logo';
      if (logo.kind === 'put') await uploadVenueLogo(saved.venueId, logo.blob);
      if (logo.kind === 'remove') await removeVenueLogo(saved.venueId);
      setEditing(null);
    } catch (caught) {
      setError(t(refusalKey(caught, stage)));
    } finally {
      // The venue is saved either way once its profile landed, so the list reads.
      if (stage === 'logo') {
        await load();
        onVenuesChanged();
      }
      setBusy(false);
    }
  }

  // The dialog stays open: managing owners is a list, not a one-shot form.
  async function register(registration: OwnerRegistration): Promise<void> {
    const tenant = owning as Tenant;
    setBusy(true);
    setError(null);
    try {
      const kept = keptNameNote(
        registration.name,
        (await registerOwner(tenant.venueId, registration)).name,
        t,
        'nameKeptWhyOwner',
      );
      if (kept !== null) push(kept, 'bg-amber-500');
      setOwning(await reloaded(tenant.venueId));
    } catch (caught) {
      const taken = caught instanceof ApiError && caught.status === 409;
      setError(t(taken ? 'ownerDuplicate' : 'ownerFailed'));
    } finally {
      setBusy(false);
    }
  }

  // §4: the name lives on the account, so this is the one place it is fixed.
  async function correctOwner(owner: TenantOwner, name: string): Promise<void> {
    const tenant = owning as Tenant;
    setBusy(true);
    setError(null);
    try {
      await renameOwner(tenant.venueId, owner.id, name);
      setOwning(await reloaded(tenant.venueId));
    } catch {
      setError(t('ownerRenameFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function drop(owner: TenantOwner): Promise<void> {
    const tenant = owning as Tenant;
    setBusy(true);
    setError(null);
    try {
      await removeOwner(tenant.venueId, owner.id);
      setOwning(await reloaded(tenant.venueId));
    } catch {
      setError(t('ownerRemoveFailed'));
    } finally {
      setBusy(false);
    }
  }

  // The list is the answer, so the dialog reads its venue back out of it.
  async function reloaded(venueId: string): Promise<Tenant | null> {
    return (await load()).find((each) => each.venueId === venueId) ?? null;
  }

  return (
    <section className="mx-auto flex w-full max-w-5xl flex-col">
      <header className="relative overflow-hidden bg-linear-to-br from-cyan-700 via-cyan-600 to-sky-300 px-6 pt-12 pb-7 text-center text-white sm:rounded-b-2xl">
        <Button
          asChild
          size="sm"
          variant="ghost"
          className="absolute top-3 left-3 text-white hover:bg-white/25 hover:text-white"
        >
          <Link to="/">
            <ChevronLeft className="size-4" aria-hidden />
            {t('back')}
          </Link>
        </Button>
        <h2 className="text-2xl font-extrabold tracking-tight sm:text-3xl">{t('heroTitle')}</h2>
        <p className="mt-1.5 text-sm opacity-85">{t('heroSub')}</p>

        <div className="mx-auto mt-4 flex max-w-md items-center gap-2 rounded-2xl bg-background p-1.5 pl-4 shadow-lg">
          <MapPin className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <input
            aria-label={t('searchTown')}
            placeholder={t('searchTown')}
            disabled
            className="min-w-0 flex-1 bg-transparent text-base text-foreground outline-none"
          />
          <Button type="button" size="sm" disabled>
            {t('searchBtn')}
          </Button>
        </div>
        <p className="mt-2 text-xs opacity-80">{t('searchSoon')}</p>

        <div className="mt-4 flex flex-wrap justify-center gap-1.5">
          {[null, ...categories].map((option) => (
            <button
              key={option ?? 'all'}
              type="button"
              aria-pressed={category === option}
              onClick={() => setCategory(option)}
              className={cn(
                'rounded-full border border-white/45 px-4 py-1.5 text-xs font-bold',
                category === option ? 'bg-background text-primary' : 'text-white',
              )}
            >
              {option === null ? t('catAll') : typeLabel(option)}
            </button>
          ))}
        </div>
      </header>

      <div className="flex flex-col gap-4 p-4">
        <div className="flex items-center gap-2">
          <h3 className="truncate text-base font-bold">{t('platformVenues')}</h3>
          <Button
            size="sm"
            className="ml-auto"
            onClick={() => {
              setError(null);
              setEditing({ tenant: null });
            }}
          >
            <Building2 className="size-4" aria-hidden />
            {t('newVenue')}
          </Button>
        </div>

        {phase !== 'ready' ? (
          <p
            role={phase === 'failed' ? 'alert' : undefined}
            className="text-sm text-muted-foreground"
          >
            {t(phase === 'failed' ? 'errorGeneric' : 'loading')}
          </p>
        ) : (
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((tenant) => (
              <VenueCard
                key={tenant.venueId}
                tenant={tenant}
                onOpen={() => onOpenVenue(tenant.slug)}
                onEdit={() => {
                  setError(null);
                  setEditing({ tenant });
                }}
                onRegisterOwner={() => {
                  setError(null);
                  setOwning(tenant);
                }}
              />
            ))}
          </ul>
        )}
      </div>

      {editing !== null && (
        <VenueDialog
          tenant={editing.tenant}
          busy={busy}
          error={error}
          onClose={() => {
            setEditing(null);
            setError(null);
          }}
          onSubmit={(profile, logo) => void save(profile, logo)}
        />
      )}

      {owning !== null && (
        <OwnerDialog
          tenant={owning}
          busy={busy}
          error={error}
          onClose={() => {
            setOwning(null);
            setError(null);
          }}
          onAdd={(registration) => void register(registration)}
          onRename={(owner, name) => void correctOwner(owner, name)}
          onRemove={(owner) => void drop(owner)}
        />
      )}
      <Toasts toasts={toasts} undoLabel={t('undo')} />
    </section>
  );
}
